import { AdminPanelLabel, AdminPanelTopic } from "../../components/TopicsPanel/types";
import { updateActiveFlag } from "../../services/patch";
import { createOrUpdateLabel, createPossibleLabelValue } from "../../services/post";
import { EndpointsWithActiveFlag } from "../../services/types";
import { PossibleLabelValue } from "../ConfigContext/types";

export const useLabelHandlers = (
  topics: AdminPanelTopic[],
  setTopics: React.Dispatch<React.SetStateAction<AdminPanelTopic[]>>,
  selectedTopic: number
) => {
  const updateLabels = (label: AdminPanelLabel, savedLabel?: AdminPanelLabel) => {
    setTopics((prevTopics) => {
      return prevTopics.map((topic) => {
        if (topic.id === selectedTopic) {
          return {
            ...topic,
            labels: savedLabel
              ? topic.labels.map((l) => {
                  if (l.id === label.id) {
                    return label.id < 0 ? savedLabel : { ...savedLabel, id: label.id };
                  } else {
                    return l;
                  }
                })
              : [label, ...topic.labels],
          };
        } else {
          return topic;
        }
      });
    });
  };

  const deleteLabel = (labelId: number) => {
    setTopics((prevTopics) => {
      return prevTopics.map((topic) => {
        if (topic.id === selectedTopic) {
          return {
            ...topic,
            labels: topic.labels.filter((l) => l.id !== labelId),
          };
        } else {
          return topic;
        }
      });
    });
  };

  const addLabelValue = (labelId: number, possibleLabelValue: PossibleLabelValue) => {
    setTopics((prevTopics) => {
      return prevTopics.map((topic) => {
        if (topic.id === selectedTopic) {
          return {
            ...topic,
            labels: topic.labels.map((label) => {
              if (label.id === labelId) {
                return {
                  ...label,
                  possibleLabelValues: [possibleLabelValue, ...label.possibleLabelValues],
                };
              } else {
                return label;
              }
            }),
          };
        } else {
          return topic;
        }
      });
    });
  };

  const updateLabelValues = (
    labelId: number,
    possibleLabelValue: PossibleLabelValue,
    newLabelValue?: PossibleLabelValue
  ) => {
    setTopics((prevTopics) => {
      return prevTopics.map((topic) => {
        if (topic.id === selectedTopic) {
          return {
            ...topic,
            labels: topic.labels.map((label) => {
              if (label.id === labelId) {
                return {
                  ...label,
                  possibleLabelValues: label.possibleLabelValues.map((plv) =>
                    plv.id === possibleLabelValue.id ? possibleLabelValue : plv
                  ),
                };
              } else {
                return label;
              }
            }),
          };
        } else {
          return topic;
        }
      });
    });
  };

  const deleteLabelValue = (labelId: number, labelValueId: number) => {
    setTopics((prevTopics) => {
      return prevTopics.map((topic) => {
        if (topic.id === selectedTopic) {
          return {
            ...topic,
            labels: topic.labels.map((label) => {
              if (label.id === labelId) {
                return {
                  ...label,
                  possibleLabelValues: label.possibleLabelValues.filter((labelValue) => labelValue.id !== labelValueId),
                };
              } else {
                return label;
              }
            }),
          };
        } else {
          return topic;
        }
      });
    });
  };

  const handleLabels = {
    add: async (label: AdminPanelLabel, saveInDb = true) => {
      if (!saveInDb) return updateLabels(label);
      try {
        const response = await createOrUpdateLabel(label);
        if (!response?.result) throw new Error("missing id of created label");
        updateLabels(label, { ...label, id: response.result });
      } catch (error) {
        console.error("Error adding new label:", error);
        return null;
      }
    },
    update: async (label: AdminPanelLabel, updateActive = false) => {
      if (!updateActive) return updateLabels(label, label);
      await updateActiveFlag(EndpointsWithActiveFlag.LABELS, label.id, label.active);
      updateLabels(label, label);
    },
    delete: async (labelId: number) => {
      // only deleting from the frontend
      deleteLabel(labelId);
    },
  };

  const handleLabelValues = {
    add: async (labelId: number, possibleLabelValue: PossibleLabelValue, saveInDb = true) => {
      if (!saveInDb) return addLabelValue(labelId, possibleLabelValue);
      const response = await createPossibleLabelValue({ labelId: labelId, value: possibleLabelValue.value });
      const newId = response.result;
      deleteLabelValue(labelId, possibleLabelValue.id);
      addLabelValue(labelId, { id: newId, value: possibleLabelValue.value });
    },
    update: async (labelId: number, possibleLabelValue: PossibleLabelValue, updateActive = false) => {
      // currently only allow updating flag
      // if (!updateActive) return updateLabelValues(labelId, possibleLabelValue, possibleLabelValue);
      await updateActiveFlag(EndpointsWithActiveFlag.LABEL_VALUES, possibleLabelValue.id, possibleLabelValue.active);
      updateLabelValues(labelId, possibleLabelValue);
    },
    delete: async (labelId: number, labelValueId: number) => {
      // only deleting from the frontend for now
      deleteLabelValue(labelId, labelValueId);
    },
  };

  return { handleLabels, handleLabelValues };
};
